import React, { useState } from 'react';

import Icon from '../../Icon';
import blackArrowDown from './assets/black-arrow-down.svg';
import DebounceInput from '../../DebounceInput/DebounceInput';
import FilterDropdownSelector from './FilterDropdownSelector/FilterDropdownSelector';
import ActiveFilters from './ActiveFilters/ActiveFilters';
import DateCampaignFilter from './DateCampaignFilter/DateCampaignFilter';
import Loader from '../../Loader/Loader';
import styles from './CampaignFilters.module.css';
import filterResultsButton from './assets/filter-results-button.svg';

const CampaignFilters = props => {
  const {
    totalCampaigns,
    availableClients,
    statuses,
    filter,
    setCampaignFilters,
    isProcessing,
    isFetching
  } = props;
  const [showFiltersContainer, setShowFiltersContainer] = useState(false);
  const campaignStatuses = Object.keys(statuses).map(statusValue => ({
    value: statusValue,
    label: statuses[statusValue]
  }));

  const webMobileOptions = [
    {
      value: 'WEB_VERSION_ONLY',
      label: 'Web version only'
    },
    {
      value: 'WEB_AND_MOBILE_VERSIONS',
      label: 'Web and mobile versions'
    }
  ];

  const filterOptions = {
    language: {
      dutch: {
        label: 'Dutch',
        value: 'Dutch'
      },
      french: {
        label: 'French',
        value: 'French'
      },
      english: {
        label: 'English',
        value: 'English'
      },
      german: {
        label: 'German',
        value: 'German'
      },
      noLanguage: {
        label: 'No language',
        value: 'noLang'
      }
    },
    date: {
      created: {
        label: 'Created',
        value: filter && filter.dates && filter.dates.created
      },
      lastResponse: {
        label: 'Last Response',
        value: filter && filter.dates && filter.dates.lastResponse
      }
    },
    community: {
      target: {
        label: 'Target',
        description: 'e.g. 200, 0-50',
        value:
          filter &&
          filter.community &&
          filter.community.target &&
          filter.community.target.value,
        onChange: value =>
          setCampaignFilters({
            ...filter,
            community: {
              ...filter.community,
              target: (value || value === '') && {
                value,
                label: value
              }
            }
          })
      },
      responses: {
        label: 'Responses',
        description: 'e.g. 200, 0-50',
        value:
          filter &&
          filter.community &&
          filter.community.responses &&
          filter.community.responses.value,
        onChange: value =>
          setCampaignFilters({
            ...filter,
            community: {
              ...filter.community,
              responses: (value || value === '') && {
                value,
                label: value
              }
            }
          })
      }
    },
    type: {
      status: {
        label: 'Status',
        placeholder: 'Choose a status',
        value: filter && filter.status && filter.status.value,
        onChange: e =>
          setCampaignFilters({
            ...filter,
            status: e.target.value && {
              value: e.target.value,
              label: statuses[e.target.value]
            }
          }),
        options: campaignStatuses
      },
      webMobile: {
        label: 'Web/Mobile',
        placeholder: 'Choose a survey type',
        value: filter && filter.webMobile && filter.webMobile.value,
        onChange: e =>
          setCampaignFilters({
            ...filter,
            webMobile: e.target.value && {
              value: e.target.value,
              label: webMobileOptions.filter(
                option => option.value === e.target.value
              )[0].label
            }
          }),
        options: webMobileOptions
      }
    },
    client: {
      email: {
        label: 'Clients',
        placeholder: 'Type a client email'
      }
    }
  };

  const onClientsSelectCompleted = clients => {
    setCampaignFilters({ ...filter, clients });
  };

  const isLanguageSelected = language => {
    const selectedLanguage =
      filter &&
      filter.language &&
      filter.language.filter(option => option.value === language);

    if (selectedLanguage && selectedLanguage.length) {
      return true;
    }
    return false;
  };

  if (!totalCampaigns && totalCampaigns !== 0) return null;

  return (
    <div className={styles.campaignsFilterContainer}>
      <div className={styles.header}>
        <div
          role="presentation"
          className={
            showFiltersContainer
              ? `${styles.filtersButton} ${styles.filtersButtonActive}`
              : styles.filtersButton
          }
          onClick={() => setShowFiltersContainer(!showFiltersContainer)}
        >
          <img src={filterResultsButton} alt="Filter result icon" />
          Filters
        </div>
        <div className={styles.campaignsCount}>
          {isProcessing || isFetching ? <Loader /> : totalCampaigns}
          <span className={styles.campaignsLabel}>surveys</span>
        </div>
      </div>
      {showFiltersContainer ? (
        <div className={styles.filtersContainer}>
          {isProcessing || isFetching ? (
            <div className={styles.filtersContainerOverlay} />
          ) : null}
          <div className={styles.filtersContainerNarrowSection}>
            <div className={styles.sectionTitle}>Language</div>
            {Object.keys(filterOptions.language).map((language, index) => (
              <div
                className={styles.languageItem}
                key={`language-container-${index.toString()}`}
                role="presentation"
                onClick={() => {
                  const { value, label } = filterOptions.language[language];

                  let filterLanguage = filter.language ? filter.language : [];
                  const selectedLanguage = filterLanguage.filter(
                    lang => lang.value === value
                  );

                  if (selectedLanguage && selectedLanguage.length) {
                    filterLanguage = filterLanguage.filter(
                      singleLanguage => singleLanguage.value !== value
                    );
                  } else {
                    filterLanguage = [...filterLanguage, { value, label }];
                  }
                  setCampaignFilters({ ...filter, language: filterLanguage });
                }}
              >
                <span
                  className={
                    isLanguageSelected(filterOptions.language[language].value)
                      ? styles.subsectionItemCheckboxSelected
                      : styles.subsectionItemCheckbox
                  }
                >
                  <Icon
                    type={
                      isLanguageSelected(filterOptions.language[language].value)
                        ? 'checked'
                        : 'unchecked'
                    }
                  />
                </span>
                <span className={styles.subsectionItemLabel}>
                  {filterOptions.language[language].label}
                </span>
              </div>
            ))}
          </div>
          <div className={styles.filtersContainerNormalSection}>
            {Object.keys(filterOptions.date).map((date, index) => (
              <DateCampaignFilter
                key={`date-campaign-filter-${index.toString()}`}
                filter={filter}
                filterOptions={filterOptions}
                date={date}
                index={index}
                setCampaignFilters={setCampaignFilters}
              />
            ))}
            <div className={styles.sectionBottomDescription}>
              Note: after and before date is included
            </div>
          </div>
          <div className={styles.filtersContainerNormalSection}>
            {Object.keys(filterOptions.community).map((community, index) => (
              <div
                className={styles.communityContainer}
                key={`community-container-${index.toString()}`}
              >
                <div className={styles.sectionTitle}>
                  {filterOptions.community[community].label}
                </div>
                <DebounceInput
                  className={styles.filterInput}
                  onKeyDown={evt => {
                    const validkeys = [
                      '1',
                      '2',
                      '3',
                      '4',
                      '5',
                      '6',
                      '7',
                      '8',
                      '9',
                      '0',
                      '-',
                      'Backspace'
                    ];
                    if (validkeys.indexOf(evt.key) < 0) {
                      evt.preventDefault();
                      return false;
                    }
                    return true;
                  }}
                  initialValue={filterOptions.community[community].value}
                  onChangeValue={filterOptions.community[community].onChange}
                />
                <div className={styles.bottomDescription}>
                  {filterOptions.community[community].description}
                </div>
              </div>
            ))}
          </div>
          <div className={styles.filtersContainerNormalSection}>
            {Object.keys(filterOptions.type).map((type, index) => (
              <div
                className={styles.typeContainer}
                key={`type-container-${index.toString()}`}
              >
                <div className={styles.sectionTitle}>
                  {filterOptions.type[type].label}
                </div>
                <select
                  className={styles.filterDropdown}
                  style={{ backgroundImage: `url(${blackArrowDown})` }}
                  value={
                    filterOptions &&
                    filterOptions.type &&
                    filterOptions.type[type] &&
                    filterOptions.type[type].value
                      ? filterOptions.type[type].value
                      : ''
                  }
                  onChange={filterOptions.type[type].onChange}
                >
                  <option value="">
                    {filterOptions.type[type].placeholder}
                  </option>
                  {filterOptions.type[type].options.map(
                    (option, optionIndex) => (
                      <option
                        value={option.value}
                        key={`filter-type-select-option-${optionIndex.toString()}`}
                      >
                        {option.label}
                      </option>
                    )
                  )}
                </select>
              </div>
            ))}
          </div>
          <div className={styles.filtersContainerWideSection}>
            {Object.keys(filterOptions.client).map((client, index) => (
              <div
                className={styles.clientContainer}
                key={`client-container-${index.toString()}`}
              >
                <div className={styles.sectionTitle}>
                  {filterOptions.client[client].label}
                </div>
                <FilterDropdownSelector
                  availableClients={availableClients.map(availableClient => ({
                    value: availableClient.id,
                    label: availableClient.email
                  }))}
                  alreadySelectedClients={filter.clients}
                  onSelectCompleted={onClientsSelectCompleted}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <ActiveFilters filter={filter} setCampaignFilters={setCampaignFilters} />
    </div>
  );
};

export default CampaignFilters;
